/**
 * SyncSection - Sección de sincronización con Supabase
 */

import { useState, useEffect } from 'react';
import { Button } from '@/ui/primitives';
import { showSuccess, showError } from '@/store';
import {
  syncAll,
  isSupabaseConfigured,
  getPendingChangesCount,
  type SyncResult,
} from '@/services/sync';
import { Cloud, CloudOff, RefreshCw, Check, AlertCircle } from 'lucide-react';

export function SyncSection() {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [isConfigured, setIsConfigured] = useState(false);

  // Verificar configuración y cambios pendientes
  useEffect(() => {
    setIsConfigured(isSupabaseConfigured());

    const checkPending = async () => {
      const count = await getPendingChangesCount();
      setPendingCount(count);
    };

    checkPending();

    // Revisar cada 30 segundos
    const interval = setInterval(checkPending, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSync = async () => {
    if (!isConfigured) {
      showError('Supabase no configurado');
      return;
    }

    setIsSyncing(true);
    try {
      const result = await syncAll();
      setLastResult(result);

      if (result.success) {
        const msg = [];
        if (result.uploaded > 0) msg.push(`${result.uploaded} subidos`);
        if (result.downloaded > 0) msg.push(`${result.downloaded} descargados`);
        if (result.conflicts > 0) msg.push(`${result.conflicts} conflictos`);

        showSuccess(msg.length > 0 ? `Sync: ${msg.join(', ')}` : 'Todo sincronizado');
        setPendingCount(0);
      } else {
        showError(result.error || 'Error de sincronización');
      }
    } catch (error) {
      showError('Error de conexión');
      console.error('Sync error:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <section>
      <h3 className="text-sm font-medium text-slate-300 mb-3">Sincronización</h3>

      <div className="p-4 rounded-lg bg-slate-800/30 space-y-4">
        {/* Estado */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            {isConfigured ? (
              <Cloud className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            ) : (
              <CloudOff className="w-5 h-5 text-slate-500 flex-shrink-0" />
            )}
            <span className="text-sm text-slate-300">
              {isConfigured ? 'Conectado' : 'No configurado'}
            </span>
          </div>

          {pendingCount > 0 && (
            <span className="text-xs text-amber-400 bg-amber-400/10 px-2 py-1 rounded flex-shrink-0">
              {pendingCount} pendiente{pendingCount > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Botón de sync */}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleSync}
          disabled={!isConfigured || isSyncing}
          className="w-full"
        >
          {isSyncing ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Sincronizando...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Sincronizar ahora
            </>
          )}
        </Button>

        {/* Último resultado */}
        {lastResult && (
          <div
            className={`flex items-center gap-2 text-xs min-w-0 ${
              lastResult.success ? 'text-emerald-400' : 'text-red-400'
            }`}
          >
            {lastResult.success ? (
              <Check className="w-4 h-4 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
            )}
            <span className="break-words">
              {lastResult.success
                ? `Subidos: ${lastResult.uploaded}, Descargados: ${lastResult.downloaded}`
                : lastResult.error}
            </span>
          </div>
        )}

        {/* Info */}
        <p className="text-xs text-slate-600">
          Los datos se sincronizan entre dispositivos automáticamente cuando hay conexión.
        </p>
      </div>
    </section>
  );
}
